import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Merge, AlertTriangle, ShieldAlert, CheckCircle2, Loader2, FileDown, RefreshCw } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { fmtDate } from '../lib/format';

const KIND_STYLE = {
  CERTO:     { label: 'Certo',     bg: 'bg-emerald-100', color: 'text-emerald-800', icon: CheckCircle2 },
  PROBABILE: { label: 'Probabile', bg: 'bg-amber-100',   color: 'text-amber-800',   icon: AlertTriangle },
  AMBIGUO:   { label: 'Ambiguo',   bg: 'bg-orange-100',  color: 'text-orange-800',  icon: AlertTriangle },
  CONFLITTO: { label: 'Conflitto', bg: 'bg-red-100',     color: 'text-red-800',     icon: ShieldAlert },
};

const KindBadge = ({ kind }) => {
  const s = KIND_STYLE[kind] || { label: kind, bg: 'bg-slate-100', color: 'text-slate-700', icon: AlertTriangle };
  const Ic = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${s.bg} ${s.color}`}>
      <Ic size={10} /> {s.label}
    </span>
  );
};

const DuplicatesPage = () => {
  const navigate = useNavigate();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [filterKind, setFilterKind] = useState('CERTO');
  const [filterCompany, setFilterCompany] = useState('');

  const [applyState, setApplyState] = useState(null); // {opId, state, phase, progress, total}

  const doScan = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/admin/duplicates/scan', {});
      setScan(data);
      toast.success(`Scansione completata: ${data.counters.pairs_certo} CERTI, ${data.counters.pairs_probabile} PROBABILI, ${data.counters.pairs_ambiguo} AMBIGUI, ${data.counters.pairs_conflitto} CONFLITTI`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore scansione');
    } finally {
      setLoading(false);
    }
  };

  const mergeableCount = scan
    ? [...(scan.pairs_certo || []), ...(scan.pairs_probabile || [])].filter((p) => p.company_id !== 'privati').length
    : 0;

  const doApplyAll = async () => {
    if (!mergeableCount) return;
    if (!window.confirm(`Confermi l'unione di ${mergeableCount} coppie (CERTE + PROBABILI)?\n\nI ruoli vengono uniti e i dati diversi finiscono nelle note della scheda principale.\n\nBackup automatico creato prima di scrivere. Operazione reversibile.`)) return;
    setApplying(true);
    setApplyState({ state: 'starting', phase: 'starting', progress: 0, total: mergeableCount });
    try {
      const { data } = await api.post('/admin/duplicates/apply-all', {
        confirm: true,
        confirmation_text: 'CONFERMA UNIONE DUPLICATI',
        plan_token: scan.token,
      });
      setApplyState(data);
      // Polling ogni 2s
      const opId = data.opId;
      if (!opId) {
        toast.info(data.message || 'Nessuna coppia da unire');
        setApplying(false);
        return;
      }
      const poll = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/admin/duplicates/apply-status/${opId}`);
          setApplyState(st);
          if (st.state === 'done') {
            clearInterval(poll);
            toast.success(`Unite ${st.pairs_applied} coppie · opId ${opId}. Rollback disponibile.`);
            setApplying(false);
            await doScan();
          } else if (st.state === 'error') {
            clearInterval(poll);
            toast.error(`Errore apply: ${st.error}`);
            setApplying(false);
          }
        } catch (err) {
          // Continua a fare polling — errore di rete transitorio
        }
      }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore apply');
      setApplying(false);
    }
  };

  const filtered = React.useMemo(() => {
    if (!scan) return [];
    const list = (scan[`pairs_${filterKind.toLowerCase()}`] || []);
    if (!filterCompany) return list;
    const s = filterCompany.toLowerCase();
    return list.filter((p) => (p.company_name || '').toLowerCase().includes(s));
  }, [scan, filterKind, filterCompany]);

  const downloadCsv = () => {
    if (!scan) return;
    const rows = [['Kind','Azienda','Principal','PrincipalCF','Duplicate','DuplicateCF','Reason','LinkedFormazione','LinkedVisite','LinkedDpi','LinkedAttachments','Conflicts']];
    ['certo','probabile','ambiguo','conflitto'].forEach((k) => {
      (scan[`pairs_${k}`] || []).forEach((p) => {
        p.duplicates.forEach((d) => {
          rows.push([
            k.toUpperCase(),
            p.company_name,
            `${p.principal.lastName || ''} ${p.principal.firstName || ''}`.trim(),
            p.principal.fiscalCode || '',
            `${d.lastName || ''} ${d.firstName || ''}`.trim(),
            d.fiscalCode || '',
            p.reason,
            p.linked_to_move?.formazione || 0,
            p.linked_to_move?.visite || 0,
            p.linked_to_move?.dpi || 0,
            p.linked_to_move?.attachments || 0,
            (p.conflicts || []).length,
          ]);
        });
      });
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `duplicati_scan_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="duplicates-page">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-sm font-semibold text-slate-700">
          <ArrowLeft size={15} /> Indietro
        </button>
        <Link to="/riconciliazione" className="text-sm text-slate-500 hover:text-[#023F80]">Riconciliazione</Link>
        <span className="text-slate-300">/</span>
        <span className="text-sm font-semibold text-slate-800">Lavoratori duplicati</span>
      </div>

      <div className="rounded-2xl bg-gradient-to-r from-[#023F80] to-[#022F60] text-white p-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
            <Users size={26} className="text-[#FFCB05]" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-extrabold">Riconciliazione lavoratori duplicati</h1>
            <p className="text-white/80 text-sm mt-2 leading-relaxed">
              Scansione automatica delle aziende per individuare lavoratori doppi: stesso token-set nome, nome/cognome invertiti, o dati complementari. Il piano viene classificato in <strong>Certi</strong> e <strong>Probabili</strong> (uniti con un click: ruoli uniti, dati diversi nelle note; esclusi i PRIVATI), <strong>Ambigui</strong> (più omonimi) e <strong>Conflitti</strong> (CF o DN differenti). Ogni unione crea un backup mirato dei soli lavoratori coinvolti ed è annullabile. L'unione parte anche in automatico a ogni import Presenze / Lavoratori.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={doScan}
          disabled={loading}
          data-testid="duplicates-scan-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#023F80] hover:bg-[#022F60] disabled:opacity-50 text-white text-sm font-bold"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          Esegui scansione (Dry-run)
        </button>
        {scan && (
          <>
            <button
              onClick={doApplyAll}
              disabled={applying || !mergeableCount}
              data-testid="duplicates-apply-all-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-bold"
            >
              {applying ? <Loader2 size={16} className="animate-spin" /> : <Merge size={16} />}
              Unisci tutti ({mergeableCount} doppioni)
            </button>
            <button onClick={downloadCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-sm font-semibold text-slate-700">
              <FileDown size={14} /> Esporta CSV
            </button>
          </>
        )}
      </div>

      {applyState && applyState.state !== 'done' && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4" data-testid="apply-progress-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Loader2 size={16} className="animate-spin text-amber-700" />
              <span className="text-sm font-bold text-amber-900">
                {applyState.phase === 'backup' && 'Creazione backup in corso…'}
                {applyState.phase === 'apply' && `Unione in corso… (${applyState.progress}/${applyState.total})`}
                {applyState.phase === 'starting' && 'Avvio operazione…'}
              </span>
            </div>
            <span className="text-[11px] font-mono text-amber-800">opId: {applyState.opId}</span>
          </div>
          <div className="h-2 bg-white rounded-full overflow-hidden">
            <div className="h-full bg-amber-500 transition-all"
                 style={{ width: `${applyState.total ? Math.round((applyState.progress / applyState.total) * 100) : (applyState.phase === 'backup' ? 5 : 0)}%` }} />
          </div>
          <div className="text-[11px] text-amber-800 mt-2">
            L'operazione prosegue anche se chiudi la pagina. Puoi riaprire e cercare lo stato in Riconciliazione → Operazioni.
          </div>
        </div>
      )}

      {scan && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Kpi label="Aziende analizzate" value={scan.counters.companies_analyzed} bg="#e0e7ff" color="#023F80" />
            <Kpi label="Lavoratori totali" value={scan.counters.workers_total} bg="#e0e7ff" color="#023F80" />
            <Kpi label="Certi" value={scan.counters.pairs_certo} bg="#dcfce7" color="#15803d" />
            <Kpi label="Probabili" value={scan.counters.pairs_probabile} bg="#fef7d4" color="#854d0e" />
            <Kpi label="Ambigui" value={scan.counters.pairs_ambiguo} bg="#ffedd5" color="#9a3412" />
            <Kpi label="Conflitti" value={scan.counters.pairs_conflitto} bg="#fee2e2" color="#b91c1c" />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs font-semibold text-slate-600">Filtri:</span>
              {['CERTO','PROBABILE','AMBIGUO','CONFLITTO'].map((k) => (
                <button
                  key={k}
                  onClick={() => setFilterKind(k)}
                  data-testid={`duplicates-filter-${k}`}
                  className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${filterKind === k ? 'bg-[#023F80] text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'}`}
                >
                  {KIND_STYLE[k].label} ({(scan[`pairs_${k.toLowerCase()}`] || []).length})
                </button>
              ))}
              <input
                type="text"
                value={filterCompany}
                onChange={(e) => setFilterCompany(e.target.value)}
                placeholder="Filtra per azienda…"
                className="ml-auto px-3 py-1 text-xs border border-slate-300 rounded-lg w-56"
                data-testid="duplicates-filter-company"
              />
            </div>

            <div className="space-y-3">
              {filtered.length === 0 && (
                <div className="p-4 text-center text-xs text-slate-400">Nessun elemento in questa categoria.</div>
              )}
              {filtered.map((p) => (
                <PairCard key={p.group_id} pair={p} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Kpi = ({ label, value, bg, color }) => (
  <div className="rounded-xl p-3" style={{ background: bg }}>
    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600">{label}</div>
    <div className="text-2xl font-extrabold mt-1" style={{ color }}>{value}</div>
  </div>
);

const PairCard = ({ pair }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`pair-${pair.group_id}`}>
    <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
      <div className="flex items-center gap-2">
        <KindBadge kind={pair.kind} />
        <span className="text-sm font-bold text-slate-800">{pair.company_name}</span>
      </div>
      <span className="text-[11px] text-slate-500 italic">{pair.reason}</span>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <PersonBox title="Principale (rimane)" bg="bg-emerald-50" border="border-emerald-200" data={pair.principal} />
      {pair.duplicates.map((d) => (
        <PersonBox key={d.id} title="Duplicato (archiviato)" bg="bg-amber-50" border="border-amber-200" data={d} />
      ))}
    </div>
    {pair.conflicts && pair.conflicts.length > 0 && (
      <div className="mt-3 p-2 rounded bg-red-50 border border-red-200">
        <div className="text-[10px] uppercase tracking-widest font-bold text-red-700">Conflitti campo ({pair.conflicts.length})</div>
        <ul className="text-[11px] text-red-800 mt-1">
          {pair.conflicts.map((c, i) => (
            <li key={i}><strong>{c.field}</strong>: "{c.principal}" vs "{c.duplicate}"</li>
          ))}
        </ul>
      </div>
    )}
    {Object.entries(pair.patch || {}).length > 0 && (
      <div className="mt-3 p-2 rounded bg-slate-50 border border-slate-200">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Verrà consolidato sul principale</div>
        <div className="text-[11px] text-slate-800 mt-1 flex flex-wrap gap-2">
          {Object.entries(pair.patch).map(([k,v]) => (
            <span key={k} className="px-2 py-0.5 rounded bg-white border border-slate-200"><strong>{k}</strong>: {String(v)}</span>
          ))}
        </div>
      </div>
    )}
    <div className="mt-3 grid grid-cols-4 gap-2">
      {Object.entries(pair.linked_to_move || {}).map(([k, v]) => (
        <div key={k} className="p-2 rounded bg-slate-100 text-center">
          <div className="text-lg font-extrabold text-[#023F80]">{v}</div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{k}</div>
        </div>
      ))}
    </div>
  </div>
);

const PersonBox = ({ title, bg, border, data }) => (
  <div className={`rounded-lg p-3 ${bg} ${border} border`}>
    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600">{title}</div>
    <div className="text-sm font-bold text-slate-900 mt-1">{data.lastName} {data.firstName}</div>
    <div className="text-[11px] text-slate-700 mt-1 space-y-0.5">
      <div>CF: <span className="font-mono">{data.fiscalCode || '—'}</span></div>
      <div>DN: {fmtDate(data.birthDate) || '—'}</div>
      <div>Mansione: {data.mansione || '—'}</div>
      <div>Sede: {data.sede || '—'}</div>
      <div>Assunzione: {fmtDate(data.dataAssunzione) || '—'}</div>
      <div className="font-mono text-[10px] text-slate-500 mt-1">id={data.id} · source={data.source || '—'}</div>
    </div>
  </div>
);

export default DuplicatesPage;
